import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Stack,
  useToast,
} from '@chakra-ui/react';
import HazardTypeResource from 'api/hazard_type';
import { CenterSpinner } from 'components/common/CenterSpinner';
import HazardTypeForm from 'components/hazardType/HazardTypeForm';
import { strings } from 'config/localization';
import routes from 'constants/routes';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';

const EditHazardType: React.FC = () => {
  const { id }: any = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();

  const methods = useForm<any>();

  const hazardAPI = new HazardTypeResource();

  const hazardTypeQuery = useQuery(
    [`hazard-type${id}-edit`, id],
    () => hazardAPI.get(id).then((res) => res?.data),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const updateHazard = useMutation((data: any) => hazardAPI.update(id, data));

  const onSubmit = async (data: any) => {
    const jsonData = {
      name: data?.name,
      description: data?.description,
    };

    updateHazard.mutate(jsonData, {
      onSuccess: (res: any) => {
        toast({
          title: strings.hazard_type_update_msg,
          status: 'success',
          isClosable: true,
        });
        queryClient.invalidateQueries('hazardList');
        history.push(routes.hazards.hazardType.list);
      },
    });
  };

  if (hazardTypeQuery.isLoading) {
    return <CenterSpinner />;
  }

  if (hazardTypeQuery.isError) {
    return (
      <Box>
        <Alert status="error">
          <AlertIcon />
          {strings.hazard_type_detail_not_found}
        </Alert>
      </Box>
    );
  }

  const hazardTypeData = hazardTypeQuery?.data?.data;

  return (
    <Stack direction="column" spacing="4">
      <Breadcrumb color="gray.400" size="4">
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to={routes.hazards.hazardType.list}>
            {strings.hazard_type}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage color="gray.900">
          <BreadcrumbLink as={RouterLink} to={routes.hazards.hazardType.edit}>
            {strings.edit_hazard_type}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex justify="space-between">
        <Heading size="md">{strings.edit_hazard_type}</Heading>
      </Flex>

      <Box bg="white" p={['3', '6']} shadow="box" rounded="sm">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack direction="column" spacing="4">
              {updateHazard.isError && (
                <Alert status="error">
                  <AlertIcon />
                  {strings.hazard_type_updated_failed}
                </Alert>
              )}
              <HazardTypeForm hazardTypeData={hazardTypeData} />
              <ButtonGroup>
                <Button
                  colorScheme="primary"
                  type="submit"
                  isLoading={updateHazard.isLoading}>
                  {strings.update}
                </Button>
                <Button variant="outline" onClick={() => history.goBack()}>
                  {strings.cancel}
                </Button>
              </ButtonGroup>
            </Stack>
          </form>
        </FormProvider>
      </Box>
    </Stack>
  );
};

export default EditHazardType;
